import { useMemo } from 'react';
import { OnboardingProfile } from '../../contexts/OnboardingContext';
import { useWorkplaceOnboardingEnabled } from './useWorkplaceOnboardingEnabled';
import { useWorkplaceOnboardingForMobileEnabled } from './useWorkplaceOnboardingForMobileEnabled';
import { User } from '@auth0/auth0-react';

export const useOnboardingUserInfo = ({
  auth0User,
  profile,
  isInvited,
  shouldPrioritizeProfile,
}: {
  profile: OnboardingProfile;
  isInvited: boolean;
  auth0User?: User | null;
  shouldPrioritizeProfile?: boolean;
}): {
  presumedEmailDomain: string;
  userFullName: string;
  userFirstName: string;
  userLastName: string;
} => {
  const isWorkplaceOnboardingForMobileEnabled =
    useWorkplaceOnboardingForMobileEnabled();
  const isWorkplaceOnboardingEnabled = useWorkplaceOnboardingEnabled();
  const isAuth0FullNameDefined =
    auth0User?.name && auth0User?.given_name && auth0User?.family_name;
  const auth0FullName = isAuth0FullNameDefined && auth0User?.name;
  const auth0FirstName = isAuth0FullNameDefined && auth0User?.given_name;
  const auth0LastName = isAuth0FullNameDefined && auth0User?.family_name;

  const presumedEmailDomain =
    auth0User?.email?.split('@')[1]?.toLowerCase() || '';

  const fullName = profile.fullName;

  const { firstName, lastName } = useMemo(() => {
    let firstName: string | undefined;
    let lastName: string | undefined;

    const userNameArr =
      fullName?.split(' ').filter(name => name.length > 0) ?? [];

    if (userNameArr.length === 1) {
      firstName = userNameArr[0];
    } else if (userNameArr.length > 1) {
      firstName = userNameArr.slice(0, -1).join(' ');
      lastName = userNameArr[userNameArr.length - 1];
    }

    return { firstName, lastName };
  }, [fullName]);

  // 1. If workplace onboarding is not enabled, return minimal info
  if (!isWorkplaceOnboardingEnabled && !isWorkplaceOnboardingForMobileEnabled) {
    return {
      presumedEmailDomain,
      userFullName: fullName ?? '',
      userFirstName: firstName ?? '',
      userLastName: lastName ?? '',
    };
  }

  const shouldUseInputtedName =
    shouldPrioritizeProfile || !isAuth0FullNameDefined;

  // 2. If user input should be prioritized or SSO info is missing, use user input
  if (shouldUseInputtedName) {
    return {
      presumedEmailDomain,
      userFullName: fullName ?? '',
      userFirstName: firstName ?? '',
      userLastName: lastName ?? '',
    };
  }

  const shouldUseAuth0Name = isInvited && !shouldUseInputtedName;

  // 3. If invited, SSO is available, and user has not updated their name, use SSO
  if (shouldUseAuth0Name) {
    return {
      presumedEmailDomain,
      userFullName: auth0FullName ?? '',
      userFirstName: auth0FirstName ?? '',
      userLastName: auth0LastName ?? '',
    };
  }

  // 4. Fallback: prefer user input and then SSO
  return {
    presumedEmailDomain,
    userFullName: fullName ?? auth0FullName ?? '',
    userFirstName: firstName ?? auth0FirstName ?? '',
    userLastName: lastName ?? auth0LastName ?? '',
  };
};
